import { lazy, Suspense } from 'react'
import type { ComponentType, ReactElement } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { LocaleLayout } from './components/LocaleLayout'
import { RootLocaleRedirect } from './components/RootLocaleRedirect'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { CookiePolicyPage } from './pages/CookiePolicyPage'
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage'
import { StationPage } from './pages/StationPage'
import { StationCardVisualFixture } from './pages/visual/StationCardVisualFixture'
import { RouteCardVisualFixture } from './pages/visual/RouteCardVisualFixture'
import { RouteDetailsVisualFixture } from './pages/visual/RouteDetailsVisualFixture'
import { StationDetailsVisualFixture } from './pages/visual/StationDetailsVisualFixture'
import { FeedbackVisualFixture } from './pages/visual/FeedbackVisualFixture'
import { StationPageHeaderVisualFixture } from './pages/visual/StationPageHeaderVisualFixture'
import { StationNavVisualFixture } from './pages/visual/StationNavVisualFixture'
import { StationServiceStatesVisualFixture } from './pages/visual/StationServiceStatesVisualFixture'

/**
 * Internal Suggester test harnesses (TASK-318) are gated behind a
 * **build-time** flag so they are tree-shaken out of the production
 * bundles entirely (security: QA H-1). The default `build`
 * script — the one Playwright's preview server runs — sets
 * `VITE_E2E_HARNESS=1`, so the e2e + visual specs reach the routes;
 * `build:production` / `build:nyc` leave it unset.
 *
 * Vite inlines `import.meta.env.VITE_E2E_HARNESS` at build time, so
 * `HARNESS_ENABLED` collapses to a literal `false` in a prod build. The
 * `lazy(() => import(...))` calls live INSIDE the `HARNESS_ENABLED ? … :
 * null` ternary, so when the flag is false the dynamic `import()` is
 * never referenced → Rollup drops the harness chunks AND their
 * transitive imports (the live §13 clients, the localStorage-seeding
 * `?favorites=` reset vector) from the output. The routes cannot be
 * reached — or even loaded — on a deployed build.
 */
const HARNESS_ENABLED = import.meta.env.VITE_E2E_HARNESS === '1'

const SuggesterVisualHarness: ComponentType | null = HARNESS_ENABLED
  ? lazy(() =>
      import('./pages/SuggesterVisualHarness').then((m) => ({
        default: m.SuggesterVisualHarness,
      })),
    )
  : null

const SuggesterE2EHarness: ComponentType | null = HARNESS_ENABLED
  ? lazy(() =>
      import('./pages/SuggesterE2EHarness').then((m) => ({
        default: m.SuggesterE2EHarness,
      })),
    )
  : null

const RouteDetailsE2EHarness: ComponentType | null = HARNESS_ENABLED
  ? lazy(() =>
      import('./pages/RouteDetailsE2EHarness').then((m) => ({
        default: m.RouteDetailsE2EHarness,
      })),
    )
  : null

/**
 * Fallback for any unmatched path UNDER a `/:locale` — redirect to that
 * locale's map (`/<locale>`). Reads the matched `locale` param explicitly
 * (a splat-relative `to="."` resolves back to the bad path and loops).
 */
function LocaleFallbackRedirect(): ReactElement {
  const { locale } = useParams<{ locale?: string }>()
  return <Navigate to={locale !== undefined ? `/${locale}` : '/'} replace />
}

function App(): ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        {/*
          /:locale/* — canonical product surface. Every visible URL the
          public web exposes is locale-prefixed. nginx (see
          infra/nginx/london-tube.com.conf) redirects `/` and any
          locale-less indexed legacy path to the user's best
          Accept-Language match; the LocaleLayout guards typoed prefixes
          (`/xx/...`) by redirecting to /en.
        */}
        <Route path="/:locale" element={<LocaleLayout />}>
          <Route element={<AppShell />}>
            <Route index element={null} />
            <Route path="stations/:slug" element={null} />
            {/*
              Canonical built-route surface (TASK-275 deep-link spec):
              `/<locale>/route?from=<slug>&to=<slug>`. Store-driven, same
              null-element pattern as `index` / `stations/:slug` — the route
              lives in the URL query; RouteSelectionUrlSync hydrates it and
              AppShell renders the map + route overlay. Matched BEFORE the
              `path="*"` locale fallback below.
            */}
            <Route path="route" element={null} />
            {/*
              Curated journey pages (`/routes/<from>-to-<to>`). These are
              minted by SiteUrls.routePath, published in the sitemap-routes-*.xml parts
              and linked from every prerendered station page — 464 URLs that
              Google has indexed and ranks (positions 4-14 as of 2026-08-04).
              Without this line they fell through to the `path="*"` fallback
              below and redirected to the locale home, so half of the site's
              search clicks landed on a page that threw the journey away.
              RouteSelectionUrlSync hydrates the pair from the path param.
            */}
            <Route path="routes/:pair" element={null} />
            {/*
              Line view (`/<locale>/lines/<slug>`, SEO-prerendered for the
              Underground lines): the map with the whole line highlighted and
              the line panel. Store-driven like `stations/:slug`; LineUrlSync
              hydrates `selectedLine` from the param. The param is
              `lineSlug`, NOT `slug` — UrlToStoreSync reads `:slug` as a
              station and `victoria` is both a line and a station. A slug
              with no line view in this city falls back to the locale map.
            */}
            <Route path="lines/:lineSlug" element={null} />
          </Route>
          {/*
            TASK-699 — assembled station-page template. Separate from the
            canonical, Google-indexed `stations/:slug` (which still drives
            the map's station panel); the URL cutover is decided when the
            template's sections are complete. Accepts NAPTAN id, slug or
            schema id + optional `?line-id=`.
          */}
          <Route path="station/:stationId" element={<StationPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="cookie-policy" element={<CookiePolicyPage />} />
          {/*
            Anything else under a locale that matches no route above — an
            empty `/stations/` (missing slug, renders blank), a typoed path —
            falls back to the locale's default page (the map). `to="."` resolves to the
            `/:locale` parent. `replace` so the bad URL isn't a history entry.
          */}
          <Route path="*" element={<LocaleFallbackRedirect />} />
        </Route>

        {/*
          Locale-less SPA entries — preserved for backwards compatibility
          with Google-indexed `https://london-tube.com/stations/<slug>` and
          `https://london-tube.com/privacy-policy` URLs that pre-date the
          /:locale/ migration. Each one client-redirects to the
          Accept-Language best match while preserving path + query.

          When nginx is in front of these routes the user never reaches
          them — nginx 301s before the SPA loads. The React-side
          redirect is the safety net for SPA-internal navigation, dev
          server access, and a future static host that lacks the nginx
          rule. See `RootLocaleRedirect`.
        */}
        <Route path="/" element={<RootLocaleRedirect preserveTail={false} />} />
        <Route path="/stations/:slug" element={<RootLocaleRedirect />} />
        {/* Canonical locale-less deep link `london-tube.com/route?from=&to=`
            → client-redirect to the Accept-Language locale, preserving the
            `?from=&to=` query (RootLocaleRedirect keeps path + query). */}
        <Route path="/route" element={<RootLocaleRedirect />} />
        <Route path="/lines/:slug" element={<RootLocaleRedirect />} />
        <Route path="/routes/:pair" element={<RootLocaleRedirect />} />
        <Route path="/privacy-policy" element={<RootLocaleRedirect />} />
        <Route path="/terms-and-conditions" element={<RootLocaleRedirect />} />
        <Route path="/cookie-policy" element={<RootLocaleRedirect />} />

        {HARNESS_ENABLED && SuggesterVisualHarness !== null && SuggesterE2EHarness !== null && (
          <>
            <Route
              path="/visual-harness/suggester"
              element={
                <Suspense fallback={null}>
                  <SuggesterVisualHarness />
                </Suspense>
              }
            />
            <Route
              path="/e2e-harness/suggester"
              element={
                <Suspense fallback={null}>
                  <SuggesterE2EHarness />
                </Suspense>
              }
            />
          </>
        )}
        {HARNESS_ENABLED && RouteDetailsE2EHarness !== null && (
          <Route
            path="/e2e-harness/routedetails"
            element={
              <Suspense fallback={null}>
                <RouteDetailsE2EHarness />
              </Suspense>
            }
          />
        )}
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/stationcard" element={<StationCardVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/routecard" element={<RouteCardVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/routedetails" element={<RouteDetailsVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/station-details" element={<StationDetailsVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/feedback" element={<FeedbackVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/station-page-header" element={<StationPageHeaderVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route path="/__visual/station-nav" element={<StationNavVisualFixture />} />
        {/* Dev/QA fixture — disallowed in robots.txt + not in sitemap. */}
        <Route
          path="/__visual/station-service-states"
          element={<StationServiceStatesVisualFixture />}
        />

        {/*
          Top-level catch-all — any locale-less path that matched nothing
          (a typo like `/xyz`, or `/stations/` with no slug + no locale)
          redirects to the Accept-Language default locale's map, dropping the
          bad tail. Mirrors the RootLocaleRedirect safety net. Must be LAST.
        */}
        <Route path="*" element={<RootLocaleRedirect preserveTail={false} />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
